import { htmlDecode } from '@jotforminc/form-common';
import { closest } from '@jotforminc/utils';
import { hasClass, triggerEvent } from './utils';

class Address {
  constructor(params) {
    // Params from cards.js
    this.cards = params.cards;
    this.layoutParams = params.layoutParams;
    this.setFieldIsFilledIfHasValue = params.setFieldIsFilledIfHasValue;
    this.updateFeedbackEmbedHeight = params.updateFeedbackEmbedHeight;
    // Class parameters
    this.autocompleteFields = [];
    this.addressTypeSuffixMap = {
      country: 'country',
      administrative_area_level_1: 'state',
      postal_code: 'postal',
      locality: 'city',
      street: 'addr_line1',
      street2: 'addr_line2',
      searchField: 'addr_search'
    };

    // Countries have different city/region types
    // US has both
    // Turkey has city
    this.countrySpecifications = {
      TR: 'city',
      GB: 'postal_town',
      DE: 'locality'
    };

    // Method binds
    this.init = this.init.bind(this);
    this.getClearField = this.getClearField.bind(this);
    this.convertToClean = this.convertToClean.bind(this);
    this.getPacContainer = this.getPacContainer.bind(this);
    this.getQuestionCard = this.getQuestionCard.bind(this);
    this.bindPacContainer = this.bindPacContainer.bind(this);
    this.showHiddenFields = this.showHiddenFields.bind(this);
    this.handleSafariProcess = this.handleSafariProcess.bind(this);
    this.getQuestionContainer = this.getQuestionContainer.bind(this);
    this.setPacContainerState = this.setPacContainerState.bind(this);
    this.clearAddressSubFields = this.clearAddressSubFields.bind(this);
    this.createPacAdditionItem = this.createPacAdditionItem.bind(this);
    this.handleGeocodingResult = this.handleGeocodingResult.bind(this);
    this.bindAutocompleteEvents = this.bindAutocompleteEvents.bind(this);
    this.handleAutocompleteResult = this.handleAutocompleteResult.bind(this);

    if (this.AutocompleteAPI) {
      // Initialization of address search fields
      this.init();
    } else {
      // Convert each address field into clean format (without autocomplete/geolocation etc.)
      this.convertToClean();
    }
  }

  getPacContainer(pacContainerObject) {
    const pacContainerKey = Object.keys(pacContainerObject).filter(key => {
      const val = pacContainerObject[key];
      return val && val.classList && val.classList.contains('pac-container');
    })[0];
    return pacContainerKey ? pacContainerObject[pacContainerKey] : {};
  }

  handleGeocodingResult(id, place) {
    // Due to geocoder api of google does not send 'adr_address' property like autocomplete, we try to find street address by using zip code placement
    const zipComponent = place.address_components.filter(address => address.types[0] === 'postal_code')[0];

    if (zipComponent && zipComponent.long_name) {
      const zipRegex = new RegExp(`.*(?=,.*${zipComponent.long_name})`);
      const streetAddress = place.formatted_address.match(zipRegex)[0];
      this.setAddressFieldValue(id, 'street', streetAddress);
    }

    // Delay is related to the fact that Safari does not fill input fields while an animation is going on
    this.handleSafariProcess(() => this.handleAddressComponents(id, place.address_components));
  }

  showHiddenFields(id) {
    const questionContainer = this.getQuestionContainer(id);
    const { cardContainer } = this.getQuestionCard(id);

    // Showing hidden address fields by removing this class
    questionContainer.classList.remove('jfAddress-clean');
    cardContainer.classList.remove('isClean');
    // work around for feedback embed button height.
    // will be removed after hackweek
    setTimeout(() => {
      this.updateFeedbackEmbedHeight();
    }, 250); // because transtions
  }

  // Binds google autocomplete to given fields and add necessary dom elements
  bindAutocompleteEvents() {
    this.autocompleteFields.forEach(({
      id, field, clearField, autocomplete, geolocationField
    }) => {
      // Binding additional pac container item events
      this.bindPacContainer(id, field, autocomplete);

      // Binding result handling
      autocomplete.addListener('place_changed', () => {
        const inputKey = Object.keys(autocomplete.gm_accessors_.place)[0];
        const pacContainerObject = autocomplete.gm_accessors_.place[inputKey].gm_accessors_.input[inputKey];
        // Object references might change and did change
        const pacContainer = this.getPacContainer(pacContainerObject);

        this.setPacContainerState(pacContainer);
        this.handleAutocompleteResult(id, field, autocomplete.getPlace());
      });

      // Binding clear event
      clearField.addEventListener('keypress', e => {
        if (e.keyCode === 32) {
          e.target.click();
        }
      });
      clearField.addEventListener('click', () => {
        this.clearAddressSubFields(id);
        this.setAddressFieldValue(id, 'searchField', '');
      });

      let inProgress = false;

      // Bind geolocation field if defined
      if (this.GeocoderAPI && this.PositionAPI && geolocationField) {
        geolocationField.addEventListener('keypress', e => {
          if (e.keyCode === 32) {
            e.target.click();
          }
        });
        geolocationField.addEventListener('click', () => {
          // Do not proceed if geolocation is in progress
          if (inProgress === true) {
            return;
          }

          // Disable it to prevent consecutive requests
          inProgress = true;

          // Loading indicator
          geolocationField.classList.add('isLoading');

          // Get address over position originated from navigator
          global.navigator.geolocation.getCurrentPosition(position => {
            const location = new this.PositionAPI(position.coords.latitude, position.coords.longitude);

            const p = new Promise((resolve, reject) => {
              const geocoder = new this.GeocoderAPI();

              geocoder.geocode({ location }, (result, status) => {
                // Enable field again
                inProgress = false;

                if (status === 'OK') {
                  return resolve(result);
                }

                return reject(status);
              });
            });

            p.then(result => {
              // Show hidden fields
              this.showHiddenFields(id);

              // Clear current data of address field
              this.clearAddressSubFields(id);

              // Gets most accured result from api response
              const placeResult = result[0];

              // Remove loading indicator
              geolocationField.classList.remove('isLoading');

              // Fill search field with given formatted address
              this.setAddressFieldValue(id, 'searchField', placeResult.formatted_address);

              this.handleGeocodingResult(id, placeResult);
            }).catch(status => {
              // Remove loading indicator
              geolocationField.classList.remove('isLoading');

              switch (status) {
                case 'ZERO_RESULTS':
                case 'OVER_QUERY_LIMIT':
                case 'REQUEST_DENIED':
                case 'INVALID_REQUEST':
                case 'UNKNOWN_ERROR':
                case 'ERROR':
                default:
                  global.alert('error while fecthing location');
                  break;
              }
            });
          });
        });
      }
    });
  }

  bindPacContainer(id, field, autocomplete) {
    // Recalling binding function until google initialize pac container
    if (!autocomplete || !autocomplete.gm_accessors_ || !autocomplete.gm_accessors_.place) {
      return setTimeout(() => this.bindPacContainer(id, field, autocomplete), 250);
    }

    // This key path for pac container seems changing depending on the client of library; therefore please do not change it based on stackoverflow or anywhere else
    const inputKey = Object.keys(autocomplete.gm_accessors_.place)[0];

    // Object references might change and did change
    const pacContainerObject = autocomplete.gm_accessors_.place[inputKey].gm_accessors_.input[inputKey];
    const pacContainer = this.getPacContainer(pacContainerObject);

    const { cardContainer } = this.getQuestionCard(id);

    // Prepare 'Enter Manually' item
    const appendDiv = this.createPacAdditionItem(pacContainer, cardContainer, id);

    // For styling
    cardContainer.classList.add('isClean', 'hasAutocomplete');
    pacContainer.classList.add('jfAddress-itemReady');
    pacContainer.appendChild(appendDiv);

    // For multiple binding usage
    const togglePacContainer = e => {
      if (pacContainer.classList.contains('hasPreviousSelection')) {
        e.target.removeEventListener('focus', togglePacContainer);
        e.target.removeEventListener('blur', togglePacContainer);
      } else {
        // pacContainer.classList[e.type === 'focus' ? 'add' : 'remove']('mustDisplay');
      }
    };

    const isMobile = this.layoutParams.deviceType === 'mobile';
    if (isMobile) {
      const addressSearch = cardContainer.querySelector('.jfAddress-search');
      if (addressSearch) {
        addressSearch.appendChild(pacContainer);
      }
    }
    // const toggleMustDisplayState = (e) => {
    //   const visibleItems = pacContainer.querySelectorAll('.pac-item:not(.pac-item-ghost)');
    //   pacContainer.classList.remove('mustDisplay');

    //   if (e.target.value) {
    //     pacContainer.classList[visibleItems.length > 0 ? 'remove' : 'add']('mustDisplay');
    //   }
    // };

    // Binding field in order to show pac container even with empty result set
    field.addEventListener('focus', togglePacContainer);
    field.addEventListener('blur', togglePacContainer);
    // field.addEventListener('keyup', toggleMustDisplayState);
  }

  setPacContainerState(pacContainer) {
    // pacContainer.classList.remove('mustDisplay');
    pacContainer.classList.add('hasPreviousSelection');
  }

  clearAddressSubFields(id) {
    Object.keys(this.addressTypeSuffixMap).forEach(suffixKey => suffixKey !== 'searchField' && this.setAddressFieldValue(id, suffixKey, ''));
  }

  handleAutocompleteResult(id, field, place) {
    // Clear current data of address field
    this.clearAddressSubFields(id);

    // Removing focus from search field for user to continue without any problem by pressing enter if there is nothing to do (We are preventing enter event on address search field)
    field.blur();

    // Removing error and validate classes to prevent user's mistaking search field as required after subfields become visible
    field.classList.remove('form-validation-error', 'validate[required]');

    // Show hidden fields
    this.showHiddenFields(id);

    // Process result address components
    if (place.address_components) {
      // Delay is related to the fact that Safari does not fill input fields while an animation is going on
      this.handleSafariProcess(() => {
        // Trying to find regex due to the fact that components of street address differ based on country that address belongs to
        const streetRegexMatch = place.adr_address ? place.adr_address.match(/<span class="street-address">(.*?)<\/span>/) : false;

        if (streetRegexMatch && streetRegexMatch[1]) {
          let streetAddressValue = htmlDecode(streetRegexMatch[1]);
          // Calculate relativity before prepending place information to the Street Address
          const relativity = this.handleRelativity(place.name, streetAddressValue);
          if (relativity < 0.5) streetAddressValue = `${place.name}, ${streetAddressValue}`;
          // If we can use the place name, then be it since it looks more verbose
          else if (place.name.length >= streetAddressValue) streetAddressValue = place.name;

          this.setAddressFieldValue(id, 'street', streetAddressValue);
        } else {
          this.setAddressFieldValue(id, 'street', place.name);
        }

        // Try to find address line 2 from google's complete address
        const extendedAddressRegexMatch = place.adr_address ? place.adr_address.match(/<span class="extended-address">(.*?)<\/span>/) : false;
        if (extendedAddressRegexMatch && extendedAddressRegexMatch[1]) {
          this.setAddressFieldValue(id, 'street2', htmlDecode(extendedAddressRegexMatch[1]));
        }

        this.handleAddressComponents(id, place.address_components);
        this.validateInputsAgain(id);
      });
    } else if (place.name) {
      // New geocoder
      const geocoder = new this.GeocoderAPI();

      // Check given streen address with geocoding and use its result if it is found; otherwise return the error
      const geocoderPromise = new Promise((resolve, reject) => geocoder.geocode({ address: place.name }, (result, status) => {
        if (status === 'OK') {
          return resolve(result);
        }

        reject(status);
      }));

      // In case geocoder retuns result, run this function with address again; otherwise fill address line 1 with written text in search
      geocoderPromise.then(result => this.handleGeocodingResult(id, result[0]))
        .catch(() => {
          this.handleSafariProcess(() => {
            this.setAddressFieldValue(id, 'street', place.name);
          });
        });
    }
  }

  validateInputsAgain(id) {
    // Current validation method is being triggered when an option has been chosen and before its components are filled
    // We need to validate that input after components took their place
    const { cardContainer } = this.getQuestionCard(id);
    const fieldsToCheck = cardContainer.querySelectorAll('*[class*="validate"]');
    fieldsToCheck.forEach(field => {
      if (field.validateInput) {
        field.validateInput();
      }
    });
  }

  handleRelativity(place, address) {
    if (place === address) return 1;
    const nameArr = place.split(' ');
    let relativity = 0;
    nameArr.forEach(name => address.indexOf(name) !== -1 && relativity++);
    return relativity / nameArr.length;
  }

  handleSafariProcess(callback, timeout = 450) {
    if (this.isSafari) {
      return setTimeout(callback, timeout);
    }
    return callback();
  }

  handleAddressComponents(id, addressComponents) {
    addressComponents.forEach(addressComponent => {
      const addressTypes = addressComponent.types;

      // Process only defined address types
      if (!addressTypes || !addressTypes[0] || !this.addressTypeSuffixMap[addressComponent.types[0]]) return;
      this.setAddressFieldValue(id, addressComponent.types[0], addressComponent.long_name);
    });

    this.adaptCityState(id, addressComponents);
  }

  get googleMapsAPI() {
    return global.google && global.google.maps;
  }

  get placesAPI() {
    return this.googleMapsAPI && this.googleMapsAPI.places;
  }

  get AutocompleteAPI() {
    return this.placesAPI && this.placesAPI.Autocomplete;
  }

  get GeocoderAPI() {
    return this.googleMapsAPI && this.googleMapsAPI.Geocoder;
  }

  get PositionAPI() {
    return this.googleMapsAPI && this.googleMapsAPI.LatLng;
  }

  get searchFields() {
    const searchFields = global.document.querySelectorAll('.form-address-search');

    return searchFields.length !== 0 ? Object.values(searchFields).filter(field => field.tagName && field.tagName === 'INPUT' && field.getAttribute('data-qid')) : [];
  }

  get isSafari() {
    return global.navigator.vendor && global.navigator.vendor.indexOf('Apple') > -1 && global.navigator.userAgent && !global.navigator.userAgent.match('CriOS');
  }

  get isFullScreen() {
    return hasClass(document.body, 'isFullScreen');
  }

  get fullScreenContainer() {
    const fullScreenContainer = document.querySelector('#jfQuestion-proxy');

    return fullScreenContainer ? fullScreenContainer : {};
  }

  getGeolocationField(id) {
    return document.querySelector(`#cid_${id} .jfAddress-geolocation`);
  }

  getClearField(id) {
    return document.querySelector(`#cid_${id} .jfAddress-clear`);
  }

  getQuestionCard(id) {
    return this.cards.filter(({ question }) => question.id === id)[0] || {};
  }

  getQuestionContainer(id) {
    const cardContainer = this.isFullScreen ? this.fullScreenContainer : this.getQuestionCard(id).cardContainer;

    return cardContainer && cardContainer.querySelector && cardContainer.querySelector('.jfQuestion-fields');
  }

  init() {
    // Construct autocomplete fields
    this.autocompleteFields = this.searchFields.map(field => {
      const id = field.getAttribute('data-qid').match(/\d+/)[0];

      return {
        id,
        field,
        clearField: this.getClearField(id),
        autocomplete: new this.AutocompleteAPI(field),
        geolocationField: this.getGeolocationField(id)
      };
    });

    // Do necessary event bindings over autocomplete search fields
    this.bindAutocompleteEvents();
  }

  convertToClean() {
    this.searchFields.forEach(field => {
      const id = field.getAttribute('data-qid').match(/\d+/)[0];
      const container = this.getQuestionContainer(id);

      // Remove unnecessary classes
      if (container && container.classList) {
        container.classList.remove('jfAddress-clean');
        container.classList.remove('jfAddress-doubleColumn');
      }

      // Remove search field
      if (field && field.remove) {
        field.remove();
      }
    });
  }

  createPacAdditionItem(pacContainer, cardContainer, id) {
    // Create main container div
    const appendDiv = global.document.createElement('div');
    appendDiv.className = 'pac-item pac-item-ghost pac-item-appended';

    // Marker container
    const markerSpan = global.document.createElement('span');
    markerSpan.className = 'pac-icon pac-item-ghost pac-icon-marker';

    // Label container
    const querySpan = global.document.createElement('span');
    querySpan.className = 'pac-item-query pac-item-ghost';
    querySpan.innerHTML = 'Enter Manually';

    // Append both marker and label before returning
    appendDiv.appendChild(markerSpan);
    appendDiv.appendChild(querySpan);

    // Bind event to open address question container manually
    appendDiv.addEventListener('mousedown', e => {
      if (e.target && e.target.classList.contains('pac-item-ghost')) {
        e.stopImmediatePropagation();

        const cleanContainer = cardContainer && cardContainer.querySelector('.jfAddress-clean');

        if (cleanContainer) {
          this.setPacContainerState(pacContainer);
          cleanContainer.classList.remove('jfAddress-clean');
        }
      }
    });

    return appendDiv;
  }

  setAddressFieldValue(id, suffixKey, val) {
    const suffix = this.addressTypeSuffixMap[suffixKey];
    const questionContainer = this.getQuestionContainer(id);
    const field = questionContainer.querySelector(`#input_${id}_${suffix}`);

    if (field) {
      field.value = val;
      this.setFieldIsFilledIfHasValue(field);

      if (suffixKey === 'postal_code') {
        const formLine = closest(field, '.form-line');
        if (formLine) { triggerEvent(formLine, 'change'); }
      }

      if (field.tagName === 'SELECT') {
        const dropdownSearchField = questionContainer.querySelector(`[data-type="${suffix}"] input`);
        if (dropdownSearchField) {
          dropdownSearchField.value = val;
          // Triggering change event manually as it does not trigger with value set
          triggerEvent(field, 'change');
        }
      }
    }
  }

  adaptCityState(id, addressComponents) {
    const addressTypes = {
      country: '',
      city: '',
      postalTown: '',
      district: '',
      sublocality: '',
      locality: ''
    };
    const cityField = document.getElementById(`input_${id}_city`);

    const addressHasAddressTypes = addressComponents.filter(address => address.types);

    addressHasAddressTypes.forEach(address => {
      switch (address.types[0]) {
        case 'country':
          addressTypes[address.types[0]] = address.short_name;
          break;
        case 'postal_town':
          addressTypes.postalTown = address.long_name;
          break;
        case 'administrative_area_level_2':
          addressTypes.city = address.long_name;
          break;
        case 'sublocality_level_1' || 'sublocality':
          addressTypes.sublocality = address.long_name;
          break;
        case 'locality':
          addressTypes.locality = address.long_name;
          break;
        default:
          if (addressTypes.district === '') {
            if (address.types.indexOf('locality') > -1) {
              addressTypes.district = address.long_name;
            } else if (address.types.indexOf('administrative_area_level_3') > -1) {
              addressTypes.district = address.long_name;
            }
          }
          break;
      }
    });

    const {
      country, city, postalTown, sublocality, locality
    } = addressTypes;
    let { district } = addressTypes;

    if (sublocality !== '' && district === '') {
      district = sublocality;
    }

    if (country in this.countrySpecifications) {
      switch (this.countrySpecifications[country]) {
        case 'city':
          if (city !== '') {
            cityField.value = city;
          } else if (district !== '') {
            cityField.value = district;
          }
          break;
        case 'postal_town':
          // Postal town countries should show it in city field
          if (postalTown !== undefined || postalTown !== '') {
            cityField.value = postalTown;
          }
          break;
        case 'locality':
          // Locality countries should show it in city field
          if (locality !== undefined || locality !== '') {
            cityField.value = locality;
          }
          break;
        default:
          break;
      }
    } else if (district !== '' && cityField.value === '') {
      cityField.value = district;
    }
    this.setFieldIsFilledIfHasValue(cityField);
  }
}

export default Address;
